import moment from 'moment';
import DTO from './DTO';
import { required } from '../utils/validation';

export const POLICY_CURRENCY_LIST = [
  { value: 'RUB', name: 'RUB' },
  { value: 'USD', name: 'USD' },
  { value: '643', name: '643' },
];

export const isPolicyEmptyValue = value => (
  value === null || typeof value === 'undefined' || value === ''
);

export const normalizePolicyArray = value => {
  if (isPolicyEmptyValue(value)) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter(item => !isPolicyEmptyValue(item));
  }
  if (typeof value === 'string') {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [value];
};

export const getPolicyRuleId = rule => {
  if (!rule || typeof rule !== 'object') {
    return rule;
  }
  return rule.rule_id || rule.id || rule.value || '';
};

export const getPolicyProviderId = provider => {
  if (!provider || typeof provider !== 'object') {
    return provider;
  }
  return provider.id || provider.provider_id || provider.value || '';
};

const normalizePolicyNullableArray = value => {
  const result = normalizePolicyArray(value);
  return result.length ? result : null;
};

const normalizePolicyIds = (value, getter) => normalizePolicyArray(value)
  .map(getter)
  .filter(item => !isPolicyEmptyValue(item))
  .map(item => String(item).trim())
  .filter(Boolean);

const createDateError = fieldName => {
  const error = new Error('Выберите корректную дату');
  error.fieldName = fieldName;
  return error;
};

export const normalizePolicyTimestamp = (value, fieldName = 'date') => {
  if (isPolicyEmptyValue(value)) {
    return null;
  }
  if (typeof value === 'number') {
    if (Number.isFinite(value)) {
      return Math.trunc(value);
    }
    throw createDateError(fieldName);
  }
  if (moment.isMoment(value)) {
    if (value.isValid()) {
      return value.valueOf();
    }
    throw createDateError(fieldName);
  }
  if (value instanceof Date) {
    const timestamp = value.getTime();
    if (Number.isFinite(timestamp)) {
      return timestamp;
    }
    throw createDateError(fieldName);
  }
  const stringValue = String(value).trim();
  if (!stringValue) {
    return null;
  }
  if (/^-?\d+$/.test(stringValue)) {
    const timestamp = Number(stringValue);
    if (Number.isFinite(timestamp)) {
      return timestamp;
    }
    throw createDateError(fieldName);
  }
  const parsed = moment(stringValue, [moment.ISO_8601, 'YYYY-MM-DD', 'DD.MM.YYYY'], true);
  if (parsed.isValid()) {
    return parsed.valueOf();
  }
  throw createDateError(fieldName);
};

const normalizePolicyString = value => (isPolicyEmptyValue(value) ? '' : String(value).trim());

const normalizePolicyCurrency = value => {
  const currency = normalizePolicyString(value);
  return currency || null;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

export const normalizePolicyPayload = (values = {}) => {
  const source = values || {};
  const payload = {};
  if (hasOwn(source, 'name')) {
    payload.name = normalizePolicyString(source.name);
  }
  if (hasOwn(source, 'policy_id') && !isPolicyEmptyValue(source.policy_id)) {
    payload.policy_id = source.policy_id;
  }
  if (hasOwn(source, 'provider_ids')) {
    payload.provider_ids = normalizePolicyNullableArray(source.provider_ids);
  }
  if (hasOwn(source, 'description')) {
    payload.description = normalizePolicyString(source.description);
  }
  if (hasOwn(source, 'currency')) {
    payload.currency = normalizePolicyCurrency(source.currency);
  }
  if (hasOwn(source, 'start_date')) {
    payload.start_date = normalizePolicyTimestamp(source.start_date, 'start_date');
  }
  if (hasOwn(source, 'end_date')) {
    payload.end_date = normalizePolicyTimestamp(source.end_date, 'end_date');
  }
  if (hasOwn(source, 'published_date')) {
    payload.published_date = normalizePolicyTimestamp(source.published_date, 'published_date');
  }
  if (hasOwn(source, 'prev_policies')) {
    payload.prev_policies = normalizePolicyNullableArray(source.prev_policies);
  }
  if (hasOwn(source, 'rules')) {
    payload.rules = normalizePolicyArray(source.rules);
  }
  if (
    !isPolicyEmptyValue(payload.start_date)
    && !isPolicyEmptyValue(payload.end_date)
    && payload.end_date < payload.start_date
  ) {
    const error = new Error('Дата окончания не может быть раньше даты начала');
    error.fieldName = 'end_date';
    throw error;
  }
  return payload;
};

export const normalizePolicyFilter = (filter = {}) => {
  if (!filter || typeof filter !== 'object') {
    return {};
  }
  const normalized = { ...filter };
  if (Object.prototype.hasOwnProperty.call(normalized, 'rules')) {
    const rules = normalizePolicyIds(normalized.rules, getPolicyRuleId);
    if (rules.length) {
      normalized.rules = rules;
    } else {
      delete normalized.rules;
    }
  }
  if (Object.prototype.hasOwnProperty.call(normalized, 'provider_ids')) {
    const providers = normalizePolicyIds(normalized.provider_ids, getPolicyProviderId);
    if (providers.length) {
      normalized.provider_ids = providers;
    } else {
      delete normalized.provider_ids;
    }
  }
  Object.keys(normalized).forEach(key => {
    const value = normalized[key];
    if (
      value === null
      || typeof value === 'undefined'
      || String(value).trim() === ''
      || String(value) === '[object Object]'
    ) {
      delete normalized[key];
    }
  });
  return normalized;
};

class PoliciesDTO extends DTO {
  name;

  policy_id;

  provider_ids;

  description;

  currency;

  start_date;

  end_date;

  published_date;

  prev_policies;

  rules;

  constructor(params, isValidInit, options = {}) {
    const { withRequired = false } = options;
    const requiredValidators = withRequired ? [required] : [];
    const attributes = [
      {
        name: 'name',
        type: String,
        validators: requiredValidators,
        labelKey: 'policies.name',
      },
      {
        name: 'policy_id',
        type: String,
        validators: [],
        labelKey: 'policies.policy_id',
      },
      {
        name: 'provider_ids',
        type: Array,
        validators: [],
        labelKey: 'policies.provider_ids',
      },
      {
        name: 'description',
        type: String,
        validators: requiredValidators,
        labelKey: 'policies.description',
      },
      {
        name: 'currency',
        type: String,
        validators: [],
        labelKey: 'policies.currency',
      },
      {
        name: 'start_date',
        type: String,
        validators: requiredValidators,
        labelKey: 'policies.start_date',
      },
      {
        name: 'end_date',
        type: String,
        validators: [],
        labelKey: 'policies.end_date',
      },
      {
        name: 'published_date',
        type: String,
        validators: requiredValidators,
        labelKey: 'policies.published_date',
      },
      {
        name: 'prev_policies',
        type: Array,
        validators: [],
        labelKey: 'policies.prev_policies',
      },
      {
        name: 'rules',
        type: Array,
        validators: requiredValidators,
        labelKey: 'policies.rules',
      },
      {
        name: 'created_at',
        type: String,
        validators: [],
        labelKey: 'created_at',
      },
    ];
    super(attributes, params, isValidInit);
  }

  getData() {
    return normalizePolicyPayload({
      name: this.name,
      policy_id: this.policy_id,
      provider_ids: this.provider_ids,
      description: this.description,
      currency: this.currency,
      start_date: this.start_date,
      end_date: this.end_date,
      published_date: this.published_date,
      prev_policies: this.prev_policies,
      rules: this.rules,
    });
  }
}

export default PoliciesDTO;
